import { useState } from "react";
import { ScheduleEditorProvider, useScheduleEditor } from "./ScheduleEditorProvider";
import { COLORS } from "./constants";
import ScheduleGrid from "./components/ScheduleGrid";
import ScheduleEditPanel from "./components/ScheduleEditPanel";
import ScheduleList from "./components/ScheduleList";

// Save/Discard toolbar - only shown when there are unsaved changes
const SaveToolbar = () => {
  const { hasUnsavedChanges, saveChanges, discardChanges } = useScheduleEditor();
  const [isSaving, setIsSaving] = useState(false);

  if (!hasUnsavedChanges) return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveChanges();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg"
      style={{ backgroundColor: COLORS.navyLight, border: `1px solid ${COLORS.white20}` }}
    >
      <span className="text-white text-sm">You have unsaved changes</span>
      <button
        onClick={discardChanges}
        disabled={isSaving}
        className="px-4 py-1.5 rounded text-sm transition-colors disabled:opacity-50"
        style={{ backgroundColor: COLORS.white20, color: COLORS.white70 }}
      >
        Discard
      </button>
      <button
        onClick={handleSave}
        disabled={isSaving}
        className="px-4 py-1.5 rounded text-sm text-white transition-colors hover:brightness-110 disabled:opacity-50"
        style={{ backgroundColor: COLORS.orange }}
      >
        {isSaving ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );
};

// Loading state
const LoadingState = () => (
  <div className="h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.navyDark }}>
    <div className="text-center">
      <div
        className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4"
        style={{ borderColor: `${COLORS.orange} transparent ${COLORS.orange} ${COLORS.orange}` }}
      />
      <p style={{ color: COLORS.white70 }}>Loading schedules...</p>
    </div>
  </div>
);

// Error state
const ErrorState = ({ error, onRetry }: { error: string; onRetry: () => void }) => (
  <div className="h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.navyDark }}>
    <div className="text-center max-w-md">
      <div className="text-red-400 text-4xl mb-4">!</div>
      <p className="text-white mb-2">Failed to load schedules</p>
      <p className="text-sm mb-4" style={{ color: COLORS.white50 }}>
        {error}
      </p>
      <button
        onClick={onRetry}
        className="px-4 py-2 rounded text-sm text-white transition-colors hover:brightness-110"
        style={{ backgroundColor: COLORS.orange }}
      >
        Try Again
      </button>
    </div>
  </div>
);

// Main layout - assembles all pieces
const ScheduleEditorLayout = () => {
  const { isLoading, error, reload } = useScheduleEditor();

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={reload} />;
  }

  return (
    <div className="h-screen flex" style={{ backgroundColor: COLORS.navyDark }}>
      {/* Main content area - scrollable */}
      <div className="flex-1 overflow-y-auto min-w-0">
        <ScheduleGrid />
        <ScheduleList />
      </div>

      {/* Sidebar - fixed position */}
      <ScheduleEditPanel />

      {/* Save toolbar - fixed position */}
      <SaveToolbar />
    </div>
  );
};

// Export wrapped with provider
export default () => (
  <ScheduleEditorProvider>
    <ScheduleEditorLayout />
  </ScheduleEditorProvider>
);
